import React, { useState, useEffect, useRef } from 'react';
import { _T, C } from '../../../shared/theme';
import { s_PB, s_GC } from '../../../shared/styles';
import { saveGameScore } from '../../../shared/utils';
import { useStorage } from '../../../shared/hooks';
import Avatar from '../../../components/ui/Avatar';
import MultiplayerInviteModal from '../../../modals/MultiplayerInviteModal';
import MultiplayerResults from '../../../modals/MultiplayerResults';

// Numbers 1–9 scattered randomly. Tap them in ascending order as fast as possible.
// 3 rounds. Score = Math.max(1, 1500 - avgRoundMs/2). Higher = faster = better.
function shuffle(arr) { return [...arr].sort(() => Math.random() - 0.5); }

const GRID_COLS = 3;

function NumChainGame({ lang, onBack, allUsers, friends, currentUserId, userName, invitePartner }) {
  const nl = lang === 'nl';
  const ROUNDS = 3;
  const N = 9;

  const [state, setState]           = useState('idle');
  const [cells, setCells]           = useState([]);   // [{num, tapped}]
  const [next, setNext]             = useState(1);
  const [round, setRound]           = useState(0);
  const [roundTimes, setRoundTimes] = useState([]);
  const [finished, setFinished]     = useState(false);
  const [best, setBest]             = useStorage('gm_numchain_best', null);
  const [showInvite, setShowInvite] = useState(false);
  const [partner, setPartner]       = useState(null);
  const [showResults, setShowResults] = useState(null);

  // Auto-set partner when arriving from game room invite
  useEffect(() => {
    if (invitePartner) { setPartner(invitePartner); }
  }, [invitePartner]); // eslint-disable-line react-hooks/exhaustive-deps

  const startRef   = useRef(null);
  const timesRef   = useRef([]);

  const makeGrid = () => {
    const nums = shuffle(Array.from({ length: N }, (_, i) => i + 1));
    setCells(nums.map(n => ({ num: n, tapped: false })));
    setNext(1);
    startRef.current = Date.now();
  };

  const startRound = (r) => {
    setRound(r);
    setState('playing');
    makeGrid();
  };

  const start = () => {
    timesRef.current = [];
    setRoundTimes([]);
    setFinished(false); setShowResults(null);
    startRound(1);
  };

  useEffect(() => {
    if (finished) {
      const avg = timesRef.current.length ? Math.round(timesRef.current.reduce((a, b) => a + b, 0) / timesRef.current.length) : 9000;
      const stored = Math.max(1, Math.round(1500 - avg / 6));
      if (!best || stored > best) setBest(stored);
      saveGameScore('numchain', stored);
      if (partner) setShowResults({ myScore: avg, partnerScore: Math.floor(Math.random() * 4000) + 3000 });
    }
  }, [finished]); // eslint-disable-line

  const tap = (i) => {
    if (state !== 'playing') return;
    const cell = cells[i];
    if (cell.tapped || cell.num !== next) return;

    const updated = cells.map((c, idx) => idx === i ? { ...c, tapped: true } : c);
    setCells(updated);
    const nextNum = next + 1;
    setNext(nextNum);

    if (nextNum > N) {
      // round complete
      const elapsed = Date.now() - startRef.current;
      timesRef.current = [...timesRef.current, elapsed];
      setRoundTimes(t => [...t, elapsed]);
      setState('roundDone');
      if (round >= ROUNDS) { setTimeout(() => setFinished(true), 600); }
      else { setTimeout(() => startRound(round + 1), 800); }
    }
  };

  const avgMs = roundTimes.length ? Math.round(roundTimes.reduce((a, b) => a + b, 0) / roundTimes.length) : null;
  const bestDisplay = best ? `${Math.max(0, (1500 - best) * 6)}ms avg` : '–';

  if (showResults) return <MultiplayerResults lang={lang} myScore={showResults.myScore} myLabel="ms/round" partnerId={partner?.id} partnerScore={showResults.partnerScore} partnerLabel="ms/round" aiComment={nl ? `Gemiddeld ${showResults.myScore}ms per ronde! Number Chain traint werkgeheugen en cognitieve snelheid.` : `Average ${showResults.myScore}ms per round! Number Chain trains working memory and cognitive speed.`} onClose={() => { setShowResults(null); setPartner(null); }} onRematch={start} />;

  return (
    <div>
      {showInvite && <MultiplayerInviteModal lang={lang} title="Number Chain" icon="🔗" allUsers={allUsers} friends={friends} onInvite={(user) => { setPartner(user); setShowInvite(false); }} onClose={() => setShowInvite(false)} />}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', color: C.silver, cursor: 'pointer', fontSize: 13 }}>← {nl ? 'Terug' : 'Back'}</button>
        <button onClick={() => setShowInvite(true)} style={{ background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, color: C.white, borderRadius: 10, padding: '5px 12px', cursor: 'pointer', fontSize: 11 }}>👥 {nl ? 'Samen' : 'Together'}</button>
      </div>
      {partner && <div style={{ ...s_GC({ padding: '8px 12px', marginBottom: 10 }), display: 'flex', alignItems: 'center', gap: 8 }}><Avatar user={partner} size={26} /><span style={{ fontSize: 12, color: C.teal }}>{partner.name} {nl ? 'speelt mee' : 'is playing'} 🟢</span></div>}

      {state === 'idle' && !finished && (
        <div style={{ ...s_GC({ textAlign: 'center', padding: 28 }) }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>🔗</div>
          <div style={{ color: C.white, fontSize: 16, fontWeight: 700, marginBottom: 8 }}>{nl ? 'Nummerketen' : 'Number Chain'}</div>
          <div style={{ color: C.silver, fontSize: 13, marginBottom: 4, lineHeight: 1.6 }}>
            {nl ? `Nummers 1–${N} verschijnen door elkaar. Tik ze in volgorde — zo snel mogelijk! ${ROUNDS} rondes.` : `Numbers 1–${N} appear scattered. Tap them in order — as fast as possible! ${ROUNDS} rounds.`}
          </div>
          {best && <div style={{ color: C.teal, fontSize: 13, margin: '12px 0' }}>🏆 Best: {bestDisplay}</div>}
          <button style={s_PB({ marginTop: 8 })} onClick={start}>{nl ? 'Starten' : 'Start'}</button>
        </div>
      )}

      {(state === 'playing' || state === 'roundDone') && !finished && (
        <>
          <div style={{ ...s_GC({ padding: '10px 16px', marginBottom: 12 }), display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, textAlign: 'center' }}>
            <div><div style={{ fontSize: 11, color: C.silver }}>{nl ? 'RONDE' : 'ROUND'}</div><div style={{ fontSize: 20, color: _T.accent, fontWeight: 700 }}>{round}/{ROUNDS}</div></div>
            <div><div style={{ fontSize: 11, color: C.silver }}>{nl ? 'VOLGENDE' : 'NEXT'}</div><div style={{ fontSize: 20, color: C.teal, fontWeight: 700 }}>{next <= N ? next : '✓'}</div></div>
            <div><div style={{ fontSize: 11, color: C.silver }}>AVG</div><div style={{ fontSize: 20, color: _T.accent, fontWeight: 700 }}>{avgMs ? `${avgMs}ms` : '–'}</div></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${GRID_COLS},1fr)`, gap: 10, marginBottom: 10 }}>
            {cells.map((cell, i) => (
              <button key={i} onClick={() => tap(i)} disabled={cell.tapped} style={{ padding: '22px 8px', background: cell.tapped ? `${C.teal}20` : cell.num === next ? `${_T.accent}25` : _T.bg4, border: `2px solid ${cell.tapped ? C.teal : cell.num === next ? _T.accent : _T.cardBorderBot}`, borderRadius: 14, cursor: cell.tapped ? 'default' : 'pointer', fontSize: 22, fontWeight: 700, color: cell.tapped ? C.teal : cell.num === next ? _T.accent : C.white, transition: 'all 0.15s' }}>
                {cell.tapped ? '✓' : cell.num}
              </button>
            ))}
          </div>
        </>
      )}

      {finished && !showResults && (
        <div style={{ ...s_GC({ textAlign: 'center', padding: 28 }) }}>
          <div style={{ fontSize: 34, color: _T.accent, fontWeight: 700, marginBottom: 4 }}>{avgMs ? `${avgMs}ms` : '–'}</div>
          <div style={{ color: C.silver, fontSize: 13, marginBottom: 12 }}>{nl ? 'gemiddelde per ronde' : 'average per round'}</div>
          <button style={s_PB()} onClick={start}>{nl ? 'Opnieuw' : 'Play again'}</button>
        </div>
      )}
    </div>
  );
}

export default NumChainGame;
